import { Alert, AlertIcon, Badge, Box, Button, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContractMessageSide,
  ViewDocumentAsOwnerDto,
  useListContractMessagesQuery,
  useMarkContractMessagesReadMutation,
  useSaveContractMessageAttachmentMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { formatDateTime, personName } from "../../../../common/components/communication/communicationUtils";
import { DownloadDocumentIconButton } from "../../../../common/documents/DownloadDocumentIconButton/DownloadDocumentIconButton";
import { useApiError } from "../../../../common/errors/useApiError";
import { useQueryParams } from "../../../../common/useQueryParams";
import { MessageComposer } from "./MessageComposer";
import { OriginCommunication } from "./OriginCommunication";

interface Props {
  contractId: string;
  documents: ViewDocumentAsOwnerDto[];
  canManageContract: boolean;
  showOriginCommunication?: boolean;
}

export const ContractDialog = ({ contractId, documents, canManageContract, showOriginCommunication }: Props) => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const toast = useToast();
  const displayer = useApiError();
  const authState = useLoggedInWithOrgContextState();
  const meId = authState.me.id;
  const { data, isLoading, refetch } = useListContractMessagesQuery({ contractId });
  const [markRead] = useMarkContractMessagesReadMutation();
  const [saveMessageAttachment] = useSaveContractMessageAttachmentMutation();
  const [savingDocumentId, setSavingDocumentId] = useState<string | null>(null);

  const messages = data?.messages;
  useEffect(() => {
    if (!messages) return;
    const unreadIncoming = messages.filter((m) => m.author.id !== meId && !m.readByMe).map((m) => m.id);
    if (unreadIncoming.length === 0) return;
    void markRead({ contractId, markMessagesReadRequest: { messageIds: unreadIncoming } })
      .unwrap()
      .then(() => refetch())
      .catch(() => undefined);
  }, [messages, meId, contractId, markRead, refetch]);

  const saveAttachment = async (messageId: string, documentId: string) => {
    setSavingDocumentId(documentId);
    try {
      const result = await saveMessageAttachment({
        contractId,
        saveMessageAttachmentRequest: { messageId, documentId },
      }).unwrap();
      toast({
        title: result.alreadyPresent ? t("Already saved to the contract") : t("Saved to the contract"),
        status: "success",
      });
      void refetch();
    } catch (e) {
      displayer.trigger(e);
    } finally {
      setSavingDocumentId(null);
    }
  };

  if (isLoading || !data) {
    return (
      <Flex py="10" justifyContent="center">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="4" maxW="900px">
      {showOriginCommunication && <OriginCommunication contractId={contractId} />}

      <Text fontSize="sm" color="smMuted">
        {t("This conversation is visible to the counterparty.")}
      </Text>

      {!data.isSharedWithCounterparty && (
        <Alert status="warning" borderRadius="md" flexDirection="column" alignItems="flex-start" gap="2">
          <Flex>
            <AlertIcon />
            {t("Share the contract with the counterparty to start a dialog.")}
          </Flex>
          <Button size="sm" variant="outline" onClick={() => params.setSelectedContractsTab("sharing")}>
            {t("Go to sharing")}
          </Button>
        </Alert>
      )}

      {data.isSharedWithCounterparty && data.messages.length === 0 && (
        <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" px="4" py="6" bg="smBackgroundSecondary">
          <Text fontWeight="semibold">{t("No messages yet")}</Text>
          <Text fontSize="sm" color="smMuted">
            {t("Send the first message to the counterparty below.")}
          </Text>
        </Box>
      )}

      <Flex direction="column" gap="3">
        {data.messages.map((message) => {
          const isOwner = message.side === ContractMessageSide.Owner;
          const isMine = message.author.id === meId;
          const isUnread = !isMine && !message.readByMe;
          return (
            <Box
              key={message.id}
              maxW="80%"
              alignSelf={isOwner ? "flex-start" : "flex-end"}
              bg={isOwner ? "smBackgroundSecondary" : "smPrimarySofter"}
              borderWidth="1px"
              borderColor={isUnread ? "smPrimary" : "smBorder"}
              borderRadius="md"
              px="4"
              py="3"
            >
              <Flex justifyContent="space-between" alignItems="center" gap="3" mb="1">
                <Flex alignItems="center" gap="2">
                  <Text fontWeight="semibold" fontSize="sm">
                    {personName(message.author)}
                  </Text>
                  {isUnread && (
                    <Badge bg="smChipBlueBg" color="smChipBlueFg">
                      {t("New")}
                    </Badge>
                  )}
                </Flex>
                <Badge
                  bg={isOwner ? "smChipBlueBg" : "smChipTealBg"}
                  color={isOwner ? "smChipBlueFg" : "smChipTealFg"}
                >
                  {isOwner ? t("Owner") : t("Counterparty")}
                </Badge>
              </Flex>
              {message.text && <Text whiteSpace="pre-wrap">{message.text}</Text>}
              {message.attachments.length > 0 && (
                <Flex direction="column" gap="1" mt="2">
                  {message.attachments.map((att) => (
                    <Flex key={att.documentId} alignItems="center" gap="2">
                      <Flex alignItems="center" gap="1">
                        <DownloadDocumentIconButton documentId={att.documentId} size="sm" />
                        <Text fontSize="sm">{att.fileName}</Text>
                      </Flex>
                      {att.savedToContract ? (
                        <Badge bg="smChipGreenBg" color="smChipGreenFg">
                          {t("Saved")}
                        </Badge>
                      ) : (
                        canManageContract && (
                          <Button
                            size="xs"
                            variant="outline"
                            isLoading={savingDocumentId === att.documentId}
                            onClick={() => void saveAttachment(message.id, att.documentId)}
                          >
                            {t("Save to contract")}
                          </Button>
                        )
                      )}
                    </Flex>
                  ))}
                </Flex>
              )}
              <Flex justifyContent="space-between" alignItems="center" mt="1" gap="3">
                <Text fontSize="xs" color="smMuted">
                  {formatDateTime(message.createdAt)}
                </Text>
                {isMine && (
                  <Text fontSize="xs" color="smMuted">
                    {message.readByOtherPartyAt
                      ? `${t("Read")} · ${formatDateTime(message.readByOtherPartyAt)}`
                      : t("Sent")}
                  </Text>
                )}
              </Flex>
            </Box>
          );
        })}
      </Flex>

      {data.canPostMessage && (
        <MessageComposer contractId={contractId} documents={documents} onSent={() => void refetch()} />
      )}
    </Flex>
  );
};
